import { Dispatch, FC, memo, SetStateAction, useContext, useMemo } from "react";
import { useRecoilValue } from "recoil";

import { usePageContext } from "@ryde/redux";
import {
  DtoFieldType,
  getTableFilteredGroupedRowsFromMap,
  GROUPING_GRID_NO_GROUPS,
  IExtendableDto,
  NO_GROUPING_QS_VALUE,
  tableFilteredGroupedRowsMapSF,
} from "@ryde/shared";

import { Grid } from "../../core";
import { TableItemContext } from "./table-item.context";
import { TableWrapperContext } from "./table-wrapper.context";
import { TableWrapper } from "./table.styled";

interface ITableItemProps {
  groupingValue:
    | IExtendableDto[keyof IExtendableDto]
    | undefined
    | null
    | typeof GROUPING_GRID_NO_GROUPS;
  collapsed?: boolean;
  setCollapsed?: Dispatch<SetStateAction<boolean>>;
  tableIndex: number;
}

const TableItemComponent: FC<ITableItemProps> = ({
  groupingValue,
  collapsed,
  setCollapsed,
  tableIndex,
}) => {
  const { entity } = usePageContext();
  const { groupingField } = useContext(TableWrapperContext);
  const groupedRowsMap = useRecoilValue(tableFilteredGroupedRowsMapSF(entity));

  const filteredTableRows = useMemo(
    () =>
      getTableFilteredGroupedRowsFromMap({
        groupedRowsMap,
        groupingFieldValue: groupingValue as
          DtoFieldType | typeof GROUPING_GRID_NO_GROUPS,
        groupingFieldNameResolved: groupingField,
      }),
    [groupedRowsMap, groupingField, groupingValue],
  );

  const tableItemContextValue = useMemo(
    () => ({ groupingValue, collapsed, setCollapsed }),
    [groupingValue, collapsed, setCollapsed],
  );

  return (
    <TableItemContext.Provider value={tableItemContextValue}>
      <TableWrapper
        data-e2e-grouping-value={groupingValue || NO_GROUPING_QS_VALUE}
      >
        <Grid<IExtendableDto>
          tableData={filteredTableRows as IExtendableDto[]}
          groupingValue={groupingValue}
          tableIndex={tableIndex}
        />
      </TableWrapper>
    </TableItemContext.Provider>
  );
};

export const TableItem = memo(TableItemComponent);
